import { Injectable } from '@jack-henry/frontend-utils/di';
import { combine, ObservationSource } from '@jack-henry/frontend-utils/observable';
import { NavigationService } from '@treasury/core/navigation';
import { CompanyUserActionService } from '@treasury/domain-components/commands/users/company-users/user-actions.service';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { CompanyService } from '@treasury/domain/backoffice/services/company';
import { AuthenticationService } from '@treasury/domain/services/authentication';
import { exists } from '@treasury/utils';

@Injectable()
export class CompanySelectionService {
    constructor(
        private readonly navigationService: NavigationService,
        private readonly companyService: CompanyService,
        private readonly userActionService: CompanyUserActionService,
        private readonly authService: AuthenticationService
    ) {
        /**
         * NOTE: We are emitting to a private local source so that our local observable
         * triggers even if the authentication service is initialized before we subscribe.
         * That is why emit last value is being used here
         */
        this.authService.authenticated$.subscribe(() => {
            if (authService.authenticated) {
                this.successfulAuthOS.emit(true);
            }
        }, true);
    }

    public company?: CompanyViewModel;

    private readonly successfulAuthOS = new ObservationSource<boolean>();

    private readonly successfulAuth$ = this.successfulAuthOS.toObservable();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly companyChange$ = combine(
        this.successfulAuth$,
        this.navigationService.routeChange$
    )
        .pipe(async () => this.getCompanyId())
        .filter(exists)
        .pipe(async companyId => this.loadCompanyInfo(companyId));

    private async getCompanyId() {
        const { params } = await this.navigationService.getRouteData<{ companyId: string }>();
        return parseInt(params.companyId);
    }

    private async loadCompanyInfo(companyId: number) {
        if (!companyId) {
            return;
        }
        const company = await this.companyService.getCompanyById(companyId, 1);
        this.company = new CompanyViewModel(company);

        await this.userActionService.loadCompanyUserActions(this.company);
    }
}
